import { StatusBreakdown } from '@/lib/api/financial/financial-dashboard-api'
import { MdHelpOutline } from 'react-icons/md'

interface StatusBreakdownGridProps {
    items: StatusBreakdown[]
    onHelp?: () => void
}

/**
 * Como chamar cada lado em cada status. "A receber" num card de CANCELADO dizia que o
 * dinheiro ainda viria; o rótulo tem de dizer o que aconteceu com ele.
 */
const SIDE_LABELS: Record<string, { receivable: string; payable: string }> = {
    PAID: { receivable: 'Recebido de clientes', payable: 'Pago a parceiros e fornecedores' },
    PENDING: { receivable: 'A receber', payable: 'A pagar' },
    OVERDUE: { receivable: 'A receber', payable: 'A pagar' },
    PARTIALLY_PAID: { receivable: 'A receber', payable: 'A pagar' },
    CANCELLED: { receivable: 'Cancelado de clientes', payable: 'Cancelado a parceiros e fornecedores' },
    REFUNDED: { receivable: 'Estornado a clientes', payable: 'Estornado de parceiros e fornecedores' },
    PARTIALLY_REFUNDED: { receivable: 'Estornado em parte a clientes', payable: 'Estornado em parte de parceiros' },
}

const formatCurrency = (cents: number) =>
    new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL',
    }).format(cents / 100)

/**
 * Lançamentos com vencimento no período, por status atual.
 *
 * Cada card separa os dois lados do caixa. Junto, "Pago" se lia como "recebido": em
 * agosto/2026 o card mostrava R$ 546,7 mil, dos quais R$ 140,7 mil eram repasses
 * pagos a parceiros — e a receita do mês sai por outra data (pagamento).
 */
export default function StatusBreakdownGrid({ items, onHelp }: StatusBreakdownGridProps) {
    const header = (
        <div className="flex items-start justify-between gap-2">
            <div>
                <h2 className="font-semibold text-base-content">Lançamentos por status</h2>
                <p className="text-sm text-base-content/60">
                    Contas a receber e a pagar com vencimento no período. Não é o que entrou — isso é a Receita.
                </p>
            </div>
            {onHelp && (
                <button
                    type="button"
                    className="btn btn-ghost btn-xs btn-circle text-base-content/40 hover:text-base-content"
                    onClick={onHelp}
                    aria-label="Como ler os lançamentos por status"
                    title="Como ler os lançamentos por status"
                >
                    <MdHelpOutline className="text-base" />
                </button>
            )}
        </div>
    )

    if (!items.length) {
        return (
            <div className="flex flex-col gap-3">
                {header}
                <div className="bg-base-100 rounded-xl border border-base-300/50 p-5 text-sm text-base-content/60">
                    Nenhum lançamento no período.
                </div>
            </div>
        )
    }

    return (
        <div className="flex flex-col gap-3">
        {header}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {items.map(item => {
                // Ticket médio DO STATUS: o valor de um lançamento típico ali.
                // Sai do próprio card (total ÷ quantidade) com a mesma conta do
                // `averageTicket` das estatísticas — não é dado novo do backend.
                const averageTicket =
                    item.count > 0 ? Math.round(item.totalAmount / item.count) : 0

                return (
                    <div
                        key={item.status}
                        className="bg-base-100 rounded-xl border border-base-300/50 p-4 flex flex-col gap-1"
                        style={{ borderLeftColor: item.color, borderLeftWidth: 4 }}
                    >
                        <div className="flex items-center justify-between">
                            <span className="text-xs uppercase tracking-wide text-base-content/60 font-medium">
                                {item.label}
                            </span>
                            <span className="text-xs text-base-content/50">
                                {item.count}
                            </span>
                        </div>
                        <p className="text-xl font-bold" style={{ color: item.color }}>
                            {formatCurrency(item.totalAmount)}
                        </p>
                        {/* Os dois lados do caixa. No status pago, o lado de clientes é o
                            que foi RECEBIDO e o de parceiros/fornecedores o que foi PAGO. */}
                        {item.receivableCount > 0 && (
                            <p className="text-xs text-base-content/70">
                                {SIDE_LABELS[item.status]?.receivable ?? 'De clientes'}:{' '}
                                <span className="font-medium">{formatCurrency(item.receivableAmount)}</span>
                                <span className="text-base-content/50"> ({item.receivableCount})</span>
                            </p>
                        )}
                        {item.payableCount > 0 && (
                            <p className="text-xs text-base-content/70">
                                {SIDE_LABELS[item.status]?.payable ?? 'A parceiros e fornecedores'}:{' '}
                                <span className="font-medium">{formatCurrency(item.payableAmount)}</span>
                                <span className="text-base-content/50"> ({item.payableCount})</span>
                            </p>
                        )}
                        {item.totalPaidAmount > 0 &&
                            item.status !== 'PAID' &&
                            item.totalPaidAmount !== item.totalAmount && (
                                <p className="text-xs text-base-content/60">
                                    {item.status === 'REFUNDED' || item.status === 'PARTIALLY_REFUNDED'
                                        ? 'Pago antes do estorno'
                                        : 'Já entrou'}
                                    : {formatCurrency(item.totalPaidAmount)}
                                </p>
                            )}
                        {item.count > 0 && (
                            <p className="text-xs text-base-content/60">
                                Ticket médio: {formatCurrency(averageTicket)}
                            </p>
                        )}
                    </div>
                )
            })}
        </div>
        </div>
    )
}
